// 📄 pages/Proyectos/Miembros.tsx
import React, { useEffect, useState } from 'react';
import { Users, DollarSign, Check, Pencil, UserMinus, UserCheck, ShieldCheck, Send } from 'lucide-react';
import { getUidsSinActivar, reenviarInvitacion } from '../../services/activacionService.ts';
import { useProject } from '../../context/ProjectContext.tsx';
import { useAuth } from '../../context/AuthContext.tsx';
import { getTarifa, setTarifa } from '../../services/tarifaService.ts';
import {
  updateMemberRole, deactivateMember, reactivateMember, getMemberPermisos, puedeGestionarPermisosDeProyecto,
  ROLE_OPTIONS, ProjectRole, ProjectMember, MemberPermisos
} from '../../services/projectService.ts';
import InvitarMiembro from './components/InvitarMiembro.tsx';
import PermisosMiembroModal from './components/PermisosMiembroModal.tsx';
import LimiteUsoAlerta from '../../components/ui/LimiteUsoAlerta.tsx';

const ROLE_LABELS: Record<string, string> = {
  PM: 'PM',
  tecnico: 'Consultor Técnico',
  funcional: 'Consultor Funcional',
  soporte: 'Soporte',
  cliente: 'Cliente'
};

export default function Miembros() {
  const { project, projectId, esCliente, esAdminObservador } = useProject();
  const { user, profile, organizations } = useAuth();

  const [miTarifa, setMiTarifa] = useState<number | null>(null);
  const [editando, setEditando] = useState(false);
  const [valorInput, setValorInput] = useState('');
  const [guardando, setGuardando] = useState(false);

  const [editandoRolUserId, setEditandoRolUserId] = useState<string | null>(null);
  const [guardandoRolUserId, setGuardandoRolUserId] = useState<string | null>(null);
  const [errorRolUserId, setErrorRolUserId] = useState<string | null>(null);

  const [cambiandoEstadoUserId, setCambiandoEstadoUserId] = useState<string | null>(null);
  const [errorEstadoUserId, setErrorEstadoUserId] = useState<string | null>(null);

  const [gestionandoPermisosDe, setGestionandoPermisosDe] = useState<ProjectMember | null>(null);

  // Quién ve el botón "Permisos": el dueño del proyecto, o a quien el
  // dueño le delegó esa facultad (ver puedeGestionarPermisosDeProyecto) —
  // a diferencia de invitar/cambiar rol/inactivar (puedeGestionar más
  // abajo), que sigue abierto a cualquier no-cliente.
  const [misPermisos, setMisPermisos] = useState<MemberPermisos | null>(null);

  // Invitados que todavía no crearon su contraseña: "Sin activar" + Reenviar.
  const [sinActivar, setSinActivar] = useState<Set<string>>(new Set());
  const [reenvio, setReenvio] = useState<{ userId: string; estado: 'enviando' | 'ok' | 'error'; mensaje?: string } | null>(null);
  const uidsMiembros = (project?.miembros || []).map((m) => m.userId).join(',');
  useEffect(() => {
    if (!uidsMiembros) return;
    getUidsSinActivar(uidsMiembros.split(','))
      .then(setSinActivar)
      .catch((err) => console.error('Error cargando invitaciones sin activar:', err));
  }, [uidsMiembros]);

  const handleReenviar = async (m: { userId: string; email: string }) => {
    setReenvio({ userId: m.userId, estado: 'enviando' });
    try {
      await reenviarInvitacion({ email: m.email, projectId });
      setReenvio({ userId: m.userId, estado: 'ok', mensaje: 'Invitación reenviada.' });
    } catch (err: any) {
      setReenvio({ userId: m.userId, estado: 'error', mensaje: err.message || 'No se pudo reenviar.' });
    }
  };

  useEffect(() => {
    if (!projectId || !user) return;
    getTarifa(projectId, user.uid)
      .then(setMiTarifa)
      .catch((err) => console.error('Error cargando tarifa:', err));
    getMemberPermisos(projectId, user.uid)
      .then(setMisPermisos)
      .catch((err) => console.error('Error cargando mis permisos:', err));
  }, [projectId, user]);

  const handleGuardarTarifa = async () => {
    if (!user) return;
    const valor = parseFloat(valorInput);
    if (isNaN(valor) || valor < 0) return;

    setGuardando(true);
    try {
      await setTarifa(projectId, user.uid, valor);
      setMiTarifa(valor);
      setEditando(false);
    } catch (err) {
      console.error('Error guardando tarifa:', err);
    } finally {
      setGuardando(false);
    }
  };

  const handleEmpezarEdicion = () => {
    setValorInput(miTarifa !== null ? String(miTarifa) : '');
    setEditando(true);
  };

  const handleCambiarRol = async (userId: string, nuevoRol: ProjectRole) => {
    setErrorRolUserId(null);
    setGuardandoRolUserId(userId);
    try {
      await updateMemberRole(projectId, userId, nuevoRol);
    } catch (err) {
      console.error('Error cambiando rol:', err);
      setErrorRolUserId(userId);
    } finally {
      setGuardandoRolUserId(null);
      setEditandoRolUserId(null);
    }
  };

  const handleInactivar = async (m: { userId: string; email: string }) => {
    if (!window.confirm(`¿Inactivar a ${m.email}? Deja de tener acceso al proyecto, pero su historial (horas, incidentes) se conserva. Podés reactivarlo cuando quieras.`)) return;
    setErrorEstadoUserId(null);
    setCambiandoEstadoUserId(m.userId);
    try {
      await deactivateMember(projectId, m.userId);
    } catch (err) {
      console.error('Error inactivando miembro:', err);
      setErrorEstadoUserId(m.userId);
    } finally {
      setCambiandoEstadoUserId(null);
    }
  };

  const handleReactivar = async (userId: string) => {
    setErrorEstadoUserId(null);
    setCambiandoEstadoUserId(userId);
    try {
      await reactivateMember(projectId, userId);
    } catch (err) {
      console.error('Error reactivando miembro:', err);
      setErrorEstadoUserId(userId);
    } finally {
      setCambiandoEstadoUserId(null);
    }
  };

  if (!project) return null;

  const esCreador = !!user && project.creadoPor === user.uid;
  const puedoGestionarPermisos = !!user && puedeGestionarPermisosDeProyecto(project, user.uid, misPermisos);
  // Owner/admin de la org del proyecto: aunque no gestione los permisos de
  // sidebar, sí da "Puede obtener credenciales" (plan-revelado-credenciales, R4).
  const rolEnOrg = organizations.find((o) => o.id === project.orgId)?.role;
  const esGestorOrg = rolEnOrg === 'owner' || rolEnOrg === 'admin';

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center gap-2 text-sm font-semibold text-gray-300">
          <Users className="w-4 h-4 text-blue-400" /> Miembros del proyecto
        </div>
        <div className="divide-y divide-white/5">
          {[...project.miembros]
            .sort((a, b) => Number(a.activo === false) - Number(b.activo === false))
            .map((m) => {
            const esUnoMismo = user?.uid === m.userId;
            const inactivo = m.activo === false;
            const puedeGestionar = !esCliente && !esAdminObservador;
            return (
              <div key={m.userId} className={`p-4 flex items-center justify-between gap-3 flex-wrap ${inactivo ? 'opacity-50' : ''}`}>
                <span className="text-sm text-white flex items-center gap-2">
                  {m.email}
                  {inactivo && (
                    <span className="text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full">Inactivo</span>
                  )}
                  {!inactivo && sinActivar.has(m.userId) && (
                    <span className="text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full" title="Todavía no creó su contraseña">Sin activar</span>
                  )}
                </span>

                <div className="flex items-center gap-3">
                  {esCliente || esAdminObservador || inactivo ? (
                    <span className="text-[10px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded-full">
                      {ROLE_LABELS[m.rol] || m.rol}
                    </span>
                  ) : editandoRolUserId === m.userId ? (
                    <select
                      autoFocus
                      value={m.rol}
                      disabled={guardandoRolUserId === m.userId}
                      onChange={(e) => handleCambiarRol(m.userId, e.target.value as ProjectRole)}
                      onBlur={() => setEditandoRolUserId(null)}
                      className="text-[11px] bg-zinc-800 border border-indigo-500/30 text-indigo-300 rounded-full px-2 py-0.5 focus:outline-none disabled:opacity-50"
                    >
                      {ROLE_OPTIONS
                        .filter((opt) => opt.value !== 'cliente' || !esUnoMismo)
                        .map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                    </select>
                  ) : (
                    <button
                      onClick={() => setEditandoRolUserId(m.userId)}
                      title="Cambiar rol"
                      className="flex items-center gap-1 text-[10px] bg-indigo-500/10 text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded-full hover:bg-indigo-500/20 transition-all"
                    >
                      {ROLE_LABELS[m.rol] || m.rol}
                      <Pencil className="w-2.5 h-2.5 opacity-60" />
                    </button>
                  )}
                  {errorRolUserId === m.userId && (
                    <span className="text-[10px] text-red-400">Error al cambiar el rol.</span>
                  )}

                  {puedeGestionar && (
                    inactivo ? (
                      <button
                        onClick={() => handleReactivar(m.userId)}
                        disabled={cambiandoEstadoUserId === m.userId}
                        title="Reactivar acceso al proyecto"
                        className="flex items-center gap-1 text-[10px] bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded-full hover:bg-emerald-500/20 transition-all disabled:opacity-50"
                      >
                        <UserCheck className="w-3 h-3" /> Reactivar
                      </button>
                    ) : (
                      <button
                        onClick={() => handleInactivar(m)}
                        disabled={cambiandoEstadoUserId === m.userId}
                        title="Inactivar (le quita el acceso, conserva el historial)"
                        className="flex items-center gap-1 text-[10px] bg-red-500/10 text-red-400 border border-red-500/20 px-2 py-0.5 rounded-full hover:bg-red-500/20 transition-all disabled:opacity-50"
                      >
                        <UserMinus className="w-3 h-3" /> Inactivar
                      </button>
                    )
                  )}
                  {errorEstadoUserId === m.userId && (
                    <span className="text-[10px] text-red-400">Error al cambiar el estado.</span>
                  )}

                  {puedeGestionar && !inactivo && sinActivar.has(m.userId) && (
                    <button
                      onClick={() => handleReenviar(m)}
                      disabled={reenvio?.userId === m.userId && reenvio.estado === 'enviando'}
                      title="Envía un correo nuevo para crear la contraseña (el enlace anterior deja de servir)"
                      className="flex items-center gap-1 text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full hover:bg-amber-500/20 transition-all disabled:opacity-50"
                    >
                      <Send className="w-3 h-3" /> Reenviar invitación
                    </button>
                  )}
                  {reenvio?.userId === m.userId && reenvio.mensaje && (
                    <span className={`text-[10px] ${reenvio.estado === 'ok' ? 'text-emerald-400' : 'text-red-400'}`}>{reenvio.mensaje}</span>
                  )}

                  {(puedoGestionarPermisos || esGestorOrg) && !inactivo && (
                    <button
                      onClick={() => setGestionandoPermisosDe(m)}
                      title="Qué secciones ve este miembro en el sidebar"
                      className="flex items-center gap-1 text-[10px] bg-white/5 text-gray-400 border border-white/10 px-2 py-0.5 rounded-full hover:bg-white/10 hover:text-white transition-all"
                    >
                      <ShieldCheck className="w-3 h-3" /> Permisos
                    </button>
                  )}

                  {esUnoMismo && !esCliente && (
                    editando ? (
                      <div className="flex items-center gap-1.5">
                        <DollarSign className="w-3.5 h-3.5 text-gray-500" />
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          autoFocus
                          value={valorInput}
                          onChange={(e) => setValorInput(e.target.value)}
                          placeholder="0.00"
                          className="w-24 bg-zinc-800 border border-white/10 rounded-lg px-2 py-1 text-xs text-white focus:outline-none focus:border-blue-500"
                        />
                        <button
                          onClick={handleGuardarTarifa}
                          disabled={guardando}
                          title="Guardar tarifa"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-emerald-400 hover:bg-white/10 transition-all disabled:opacity-40"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={handleEmpezarEdicion}
                        title="Tarifa por hora (solo la ves tú)"
                        className="flex items-center gap-1.5 text-[11px] text-gray-400 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 px-2.5 py-1 rounded-full transition-all"
                      >
                        <DollarSign className="w-3 h-3" />
                        {miTarifa !== null ? `${miTarifa.toLocaleString('es', { maximumFractionDigits: 2 })}/h` : 'Definir tarifa'}
                        <Pencil className="w-3 h-3 opacity-60" />
                      </button>
                    )
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {!esCliente && !esAdminObservador && (
        <>
          {/* Solo el CREADOR del proyecto puede leer su propio límite
              (users/{uid} es self-or-admin en firestore.rules) — otro
              miembro que gestiona Miembros no ve esta alerta, pero igual
              queda protegido por la regla real al invitar. */}
          {esCreador && (
            <LimiteUsoAlerta
              cantidad={project.miembrosUids.length}
              limite={profile?.limiteMiembrosPorProyecto}
              etiqueta="miembros"
            />
          )}
          <InvitarMiembro
            projectId={projectId}
            orgId={project.orgId}
            miembrosActualesIds={project.miembros.map((m) => m.userId)}
          />
        </>
      )}

      {gestionandoPermisosDe && (
        <PermisosMiembroModal
          projectId={projectId}
          project={project}
          miembro={gestionandoPermisosDe}
          esCreador={esCreador}
          soloCredenciales={!puedoGestionarPermisos}
          onClose={() => setGestionandoPermisosDe(null)}
          onSaved={() => setGestionandoPermisosDe(null)}
        />
      )}
    </div>
  );
}
